import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

export const DEFAULT_SOURCES = ['Website manual capture', 'WhatsApp order', 'Phone order', 'Instagram order'];
export const DEFAULT_PAYMENT_METHODS = [
  { code: 'card_paid', label: 'Card paid' },
  { code: 'cash_on_delivery', label: 'Cash on delivery' },
  { code: 'eft_pending', label: 'EFT pending' },
];
export const DEFAULT_STORES = [
  { id: 'bothasig', name: 'Bothasig', deliveryFee: 55, prepMinutes: 10, maxKm: 12, active: true, address: '', addressPlaceId: '', geo: null },
  { id: 'district-six', name: 'District Six', deliveryFee: 55, prepMinutes: 10, maxKm: 12, active: true, address: '', addressPlaceId: '', geo: null },
  { id: 'mowbray', name: 'Mowbray', deliveryFee: 55, prepMinutes: 10, maxKm: 12, active: true, address: '', addressPlaceId: '', geo: null },
  { id: 'parow', name: 'Parow', deliveryFee: 55, prepMinutes: 10, maxKm: 12, active: true, address: '', addressPlaceId: '', geo: null },
];

export const DEFAULT_STORE_CONFIG = Object.fromEntries(
  DEFAULT_STORES.map((store) => [store.name, {
    active: store.active,
    deliveryFee: store.deliveryFee,
    prepMinutes: store.prepMinutes,
    maxDeliveryKm: store.maxKm,
    address: store.address,
    placeId: store.addressPlaceId,
    lat: store.geo?.lat ?? null,
    lng: store.geo?.lng ?? null,
  }]),
);

export const DEFAULT_ONTIME_SETTINGS = {
  defaultDeliveryFee: 55,
  defaultPrepMinutes: 10,
  stores: DEFAULT_STORES,
  sources: DEFAULT_SOURCES,
  paymentMethods: DEFAULT_PAYMENT_METHODS,
  archives: {
    stores: [],
    sources: [],
    paymentMethods: [],
    customers: [],
    drivers: [],
    onlineTeam: [],
  },
  deliveryTargetMinutes: 60,
  onlineTargetMinutes: 10,
  storeHandoffCardTheme: 'dark',
  onlineTeam: [],
  drivers: [],
};

export function slugify(value = '') {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `item-${Date.now()}`;
}

export function makeArchiveEntry(type, keyOrData, dataOrActor, maybeActor) {
  let key;
  let data;
  let actor;

  if (typeof keyOrData === 'string' && maybeActor !== undefined) {
    key = keyOrData;
    data = dataOrActor;
    actor = maybeActor;
  } else {
    data = keyOrData;
    actor = dataOrActor;
    key = data?.name || data?.label || data?.value || data?.code || data?.id || `${type}-${Date.now()}`;
  }

  const now = Date.now();
  return {
    id: `${type}-${slugify(String(key))}-${now}`,
    key: String(key),
    type,
    archivedAt: now,
    archivedBy: actor || 'unknown',
    data,
  };
}

export function coerceNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

export function normalizeCoord(value) {
  if (value === '' || value === null || typeof value === 'undefined') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function sortByName(arr = []) {
  return [...arr].sort((a, b) => String(a?.name || a?.label || a?.key || a || '').localeCompare(String(b?.name || b?.label || b?.key || b || '')));
}

function normalizeStore(store = {}, fallbackName = 'Store', defaults = {}) {
  const name = String(store.name || fallbackName).trim() || fallbackName;
  return {
    id: String(store.id || slugify(name)),
    name,
    deliveryFee: coerceNumber(store.deliveryFee, defaults.defaultDeliveryFee ?? 55),
    prepMinutes: coerceNumber(store.prepMinutes, defaults.defaultPrepMinutes ?? 10),
    maxKm: coerceNumber(store.maxKm ?? store.maxDeliveryKm, 12),
    active: store.active !== false,
    address: String(store.address || '').trim(),
    addressPlaceId: String(store.addressPlaceId || store.placeId || '').trim(),
    geo: normalizeCoord(store.geo?.lat ?? store.lat) != null && normalizeCoord(store.geo?.lng ?? store.lng) != null
      ? { lat: normalizeCoord(store.geo?.lat ?? store.lat), lng: normalizeCoord(store.geo?.lng ?? store.lng) }
      : null,
  };
}

function normalizeSources(list = []) {
  const unique = Array.from(new Set((Array.isArray(list) ? list : []).map((v) => String(v || '').trim()).filter(Boolean)));
  return unique.sort((a, b) => a.localeCompare(b));
}

function normalizePayments(list = []) {
  const cleaned = (Array.isArray(list) ? list : []).map((item) => {
    if (typeof item === 'string') {
      const label = item.trim();
      return label ? { code: slugify(label).replace(/-/g, '_'), label } : null;
    }
    const label = String(item?.label || item?.value || item?.code || '').trim();
    const code = String(item?.code || item?.value || slugify(label).replace(/-/g, '_')).trim();
    return label && code ? { code, label } : null;
  }).filter(Boolean);

  const seen = new Set();
  return cleaned.filter((item) => {
    if (seen.has(item.code)) return false;
    seen.add(item.code);
    return true;
  }).sort((a, b) => a.label.localeCompare(b.label));
}

function normalizeTeam(list = []) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((member) => ({
      id: String(member?.id || `${member?.role || 'online'}-${slugify(member?.name || 'user')}`),
      name: String(member?.name || '').trim(),
      phone: String(member?.phone || '').trim(),
      store: String(member?.store || member?.area || '').trim(),
      email: String(member?.email || member?.contact || '').trim(),
      active: member?.active !== false,
      role: String(member?.role || 'online'),
      createdAt: member?.createdAt || null,
      createdBy: member?.createdBy || '',
    }))
    .filter((member) => member.name)
    .filter((member) => {
      if (seen.has(member.id)) return false;
      seen.add(member.id);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeArchiveItems(list = [], type) {
  return sortByName((Array.isArray(list) ? list : []).map((entry) => {
    if (entry && typeof entry === 'object' && entry.data) {
      return {
        id: entry.id || `${type}-${Date.now()}`,
        key: entry.key || entry.data?.name || entry.data?.label || entry.data?.value || entry.data?.code || entry.id || type,
        type: entry.type || type,
        archivedAt: entry.archivedAt || null,
        archivedBy: entry.archivedBy || '',
        data: entry.data,
      };
    }
    return makeArchiveEntry(type, typeof entry === 'string' ? { value: entry, name: entry } : entry, 'legacy');
  }));
}

function normalizeArchives(raw = {}) {
  return {
    stores: normalizeArchiveItems(raw?.stores || [], 'stores'),
    sources: normalizeArchiveItems(raw?.sources || [], 'sources'),
    paymentMethods: normalizeArchiveItems(raw?.paymentMethods || [], 'paymentMethods'),
    customers: normalizeArchiveItems(raw?.customers || [], 'customers'),
    drivers: normalizeArchiveItems(raw?.drivers || [], 'drivers'),
    onlineTeam: normalizeArchiveItems(raw?.onlineTeam || [], 'onlineTeam'),
  };
}

export function normalizeSettings(raw = {}) {
  const incoming = raw && typeof raw === 'object' ? raw : {};
  const defaultDeliveryFee = coerceNumber(incoming.defaultDeliveryFee, DEFAULT_ONTIME_SETTINGS.defaultDeliveryFee);
  const defaultPrepMinutes = coerceNumber(incoming.defaultPrepMinutes, DEFAULT_ONTIME_SETTINGS.defaultPrepMinutes);

  let stores = [];
  if (Array.isArray(incoming.stores)) {
    stores = incoming.stores.map((store) => normalizeStore(store, store?.name || 'Store', { defaultDeliveryFee, defaultPrepMinutes }));
  } else if (incoming.stores && typeof incoming.stores === 'object') {
    stores = Object.entries(incoming.stores).map(([name, store]) => normalizeStore(store, name, { defaultDeliveryFee, defaultPrepMinutes }));
  } else {
    stores = DEFAULT_STORES.map((store) => normalizeStore(store, store.name, { defaultDeliveryFee, defaultPrepMinutes }));
  }

  return {
    defaultDeliveryFee,
    defaultPrepMinutes,
    stores: sortByName(stores),
    sources: normalizeSources(incoming.sources?.length ? incoming.sources : DEFAULT_SOURCES),
    paymentMethods: normalizePayments(incoming.paymentMethods?.length ? incoming.paymentMethods : DEFAULT_PAYMENT_METHODS),
    archives: normalizeArchives(incoming.archives),
    deliveryTargetMinutes: coerceNumber(incoming.deliveryTargetMinutes, DEFAULT_ONTIME_SETTINGS.deliveryTargetMinutes),
    onlineTargetMinutes: coerceNumber(incoming.onlineTargetMinutes, DEFAULT_ONTIME_SETTINGS.onlineTargetMinutes),
    storeHandoffCardTheme: incoming.storeHandoffCardTheme === 'light' ? 'light' : 'dark',
    onlineTeam: normalizeTeam(incoming.onlineTeam || []),
    drivers: normalizeTeam(incoming.drivers || []),
  };
}

export const normalizeOnTimeSettings = normalizeSettings;

function toFirestoreShape(settings, existing = {}) {
  const normalized = normalizeSettings({ ...existing, ...settings });
  const existingNormalized = normalizeSettings(existing);
  const incomingOnlineTeam = normalizeTeam(settings?.onlineTeam || []);
  return {
    defaultDeliveryFee: normalized.defaultDeliveryFee,
    defaultPrepMinutes: normalized.defaultPrepMinutes,
    stores: normalized.stores,
    sources: normalized.sources,
    paymentMethods: normalized.paymentMethods,
    archives: normalized.archives,
    deliveryTargetMinutes: normalized.deliveryTargetMinutes,
    onlineTargetMinutes: normalized.onlineTargetMinutes,
    storeHandoffCardTheme: normalized.storeHandoffCardTheme,
    onlineTeam: incomingOnlineTeam.length ? incomingOnlineTeam : existingNormalized.onlineTeam,
    drivers: normalizeTeam(settings?.drivers || []).length ? normalizeTeam(settings.drivers || []) : (existingNormalized.drivers || []),
  };
}

export async function loadOnTimeSettings(orgId) {
  if (!orgId) return normalizeSettings();
  const snap = await getDoc(doc(db, 'orgs', orgId, 'ontimeSettings', 'general'));
  return normalizeSettings(snap.exists() ? snap.data() : {});
}

export async function saveOnTimeSettings(orgId, settings, actor) {
  if (!orgId) throw new Error('Missing orgId');
  const ref = doc(db, 'orgs', orgId, 'ontimeSettings', 'general');
  const existingSnap = await getDoc(ref);
  const existing = existingSnap.exists() ? existingSnap.data() : {};
  const payload = toFirestoreShape(settings, existing);
  await setDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp(),
    ...(actor ? { updatedBy: actor } : {}),
  }, { merge: true });
}

export function getStoreByName(settings, name) {
  const stores = normalizeSettings(settings).stores;
  return stores.find((store) => store.name === name) || null;
}

export function getStoreConfig(settings, storeName) {
  return getStoreByName(settings, storeName) || normalizeStore({ name: storeName || 'Store' }, storeName || 'Store', normalizeSettings(settings));
}

export function getActiveStoreNames(settings) {
  return normalizeSettings(settings).stores.filter((store) => store.active).map((store) => store.name);
}

export function getDeliveryFeeForStore(settings, storeName) {
  return Number(getStoreByName(settings, storeName)?.deliveryFee ?? normalizeSettings(settings).defaultDeliveryFee ?? 0);
}

export function haversineKm(a, b) {
  if (!a || !b) return null;
  const lat1 = Number(a.lat);
  const lng1 = Number(a.lng);
  const lat2 = Number(b.lat);
  const lng2 = Number(b.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const q =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * (2 * Math.atan2(Math.sqrt(q), Math.sqrt(1 - q)));
}

export function findNearestStore(settings, customerGeo) {
  const ranked = normalizeSettings(settings).stores
    .filter((store) => store.active && store.geo)
    .map((store) => ({ ...store, distanceKm: haversineKm(customerGeo, store.geo) }))
    .filter((store) => Number.isFinite(store.distanceKm))
    .sort((a, b) => a.distanceKm - b.distanceKm);
  return ranked[0] || null;
}

export function evaluateRouting(settings, selectedStoreName, customerGeo) {
  const normalized = normalizeSettings(settings);
  const selectedStore = normalized.stores.find((store) => store.name === selectedStoreName) || null;
  const nearestStore = findNearestStore(normalized, customerGeo);
  const distanceKm = selectedStore?.geo ? haversineKm(customerGeo, selectedStore.geo) : null;
  const withinRange = Number.isFinite(distanceKm) && selectedStore
    ? distanceKm <= Number(selectedStore.maxKm || 0)
    : null;

  const nearestValidStore = normalized.stores
    .filter((store) => store.active && store.geo)
    .map((store) => ({ ...store, distanceKm: haversineKm(customerGeo, store.geo) }))
    .filter((store) => Number.isFinite(store.distanceKm) && store.distanceKm <= Number(store.maxKm || 0))
    .sort((a, b) => a.distanceKm - b.distanceKm)[0] || null;

  return {
    selectedStore,
    nearestStore,
    nearestValidStore,
    distanceKm,
    withinRange,
  };
}

export function sortStoresAZ(values = []) {
  return [...values].sort((a, b) => String(a?.name || a?.[0] || '').localeCompare(String(b?.name || b?.[0] || '')));
}

export function sortStringsAZ(values = []) {
  return [...values].sort((a, b) => String(a || '').localeCompare(String(b || '')));
}

export function sortPaymentMethodsAZ(values = []) {
  return [...values].sort((a, b) => String(a?.label || a?.value || a?.code || '').localeCompare(String(b?.label || b?.value || b?.code || '')));
}
